import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { Container, Title, Text, Button, Alert } from '@mantine/core';
import { Check, CheckCircle, DownloadSimple, ArrowLeft, Lock } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../utils/api';

export default function EbookLandingPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ email: '', phone: '', accepted: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get('/accounts/ebooks/')
      .then((r) => setEbook((r.data.ebooks || []).find((e) => e.slug === slug) || null))
      .catch(() => setEbook(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email) { setError(t('ebooks.errorEmailRequired')); return; }
    if (!form.accepted) { setError(t('ebooks.errorConsent')); return; }
    setSending(true);
    try {
      const r = await api.post('/accounts/ebooks/subscribe/', {
        email: form.email,
        phone: form.phone,
        ebook: slug,
        accepted_terms: form.accepted,
        source: 'landing',
      });
      // Ebook dostarczamy wyłącznie mailem (PDF w załączniku), bez pobierania w przeglądarce.
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.error;
      setError(msg || t('ebooks.errorGeneric'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F5F4]">
        <Navbar />
        <Container size="xl" className="pt-32 pb-24">
          <Text ta="center" c="dimmed">{t('ebooks.loading')}</Text>
        </Container>
        <Footer />
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className="min-h-screen bg-[#F6F5F4]">
        <SEO title={t('ebooks.notFoundSeoTitle')} path={`/ebooki/${slug}`} noIndex />
        <Navbar />
        <Container size="xl" className="pt-32 pb-24">
          <div className="max-w-md mx-auto text-center">
            <Title order={2} mb="sm">{t('ebooks.notFoundTitle')}</Title>
            <Text c="dimmed" mb="lg">{t('ebooks.notFoundText')}</Text>
            <Button component={Link} to="/ebooki" color="brand" leftSection={<ArrowLeft size={16} />}>
              {t('ebooks.allEbooks')}
            </Button>
          </div>
        </Container>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO
        title={ebook.title}
        description={`${ebook.blurb} ${t('ebooks.seoDescriptionSuffix')}`}
        path={`/ebooki/${ebook.slug}`}
      />
      <Navbar />

      <section className="pt-28 pb-24">
        <Container size="xl">
          <Link to="/ebooki" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 transition mb-8">
            <ArrowLeft size={16} /> {t('ebooks.allEbooks')}
          </Link>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start max-w-6xl mx-auto">
            {/* Left: cover + what's inside */}
            <div>
              <div className="flex flex-col sm:flex-row gap-7">
                <img
                  src={ebook.cover}
                  alt={ebook.title}
                  className="w-44 shrink-0 rounded-lg shadow-xl mx-auto sm:mx-0"
                  style={{ aspectRatio: '210 / 297', objectFit: 'cover' }}
                />
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600">
                    {ebook.platform}
                  </span>
                  <Title order={1} fz={{ base: 24, md: 30 }} mt={6} mb="sm" style={{ letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                    {ebook.title}
                  </Title>
                  <Text c="dimmed" fz={15} lh={1.6}>{ebook.blurb}</Text>
                </div>
              </div>

              <div className="mt-9">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-4">
                  {t('ebooks.whatInside')}
                </h2>
                <ul className="space-y-3">
                  {ebook.chapters.map((c, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 shrink-0 w-5 h-5 rounded-md bg-indigo-100 flex items-center justify-center">
                        <Check size={13} weight="bold" className="text-indigo-600" />
                      </span>
                      <span className="text-[15px] text-gray-700 leading-snug">{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right: signup form */}
            <div className="lg:sticky lg:top-28">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
                {sent ? (
                  <div className="text-center py-4">
                    <CheckCircle size={44} weight="fill" className="text-green-500 mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-1.5">{t('ebooks.sentTitle')}</h3>
                    <p className="text-sm text-gray-500 mb-2 leading-relaxed">
                      {t('ebooks.sentText')}
                    </p>
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <p className="text-sm text-gray-600 mb-3">
                        {t('ebooks.sentCtaText')}
                      </p>
                      <Button component={Link} to="/rejestracja" variant="light" color="brand" fullWidth radius="md">
                        {t('ebooks.trialButton')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-gray-900 mb-1.5">{t('ebooks.downloadFree')}</h3>
                    <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                      {t('ebooks.formSubtitle')}
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {error && <Alert color="red" variant="light"><Text size="sm">{error}</Text></Alert>}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('ebooks.emailLabel')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition"
                          placeholder={t('ebooks.emailPlaceholder')}
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('ebooks.phoneLabel')} <span className="text-gray-400 font-normal">{t('ebooks.optional')}</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition"
                          placeholder={t('ebooks.phonePlaceholder')}
                        />
                      </div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.accepted}
                          onChange={(e) => setForm({ ...form, accepted: e.target.checked })}
                          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-[13px] text-gray-500 leading-snug">
                          {t('ebooks.consentText')}{' '}
                          <Link to="/polityka-prywatnosci" className="text-indigo-600 underline underline-offset-2">
                            {t('ebooks.privacyPolicy')}
                          </Link>.
                        </span>
                      </label>
                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        color="brand"
                        loading={sending}
                        leftSection={<DownloadSimple size={18} weight="bold" />}
                      >
                        {t('ebooks.submitButton')}
                      </Button>
                      <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
                        <Lock size={12} weight="fill" />
                        {t('ebooks.noSpam')}
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
